import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '@/lib/supabase/cliente'
import type { InsumoMaestro, TipoInsumoMaestro } from '../presupuestos/tipos'
import { INSUMOS_INICIALES } from './datosInicialesInsumos'
import type { Apu } from '../apus/tipos'
import { recalcularApu } from '../apus/tipos'

const sb: any = supabase
const CLAVE_LOCAL_INSUMOS = 'mega_maestro_insumos_v1'
const CLAVE_LOCAL_APUS = 'mega_banco_apus_v1'

function cargarLocal(): InsumoMaestro[] {
  try {
    const raw = localStorage.getItem(CLAVE_LOCAL_INSUMOS)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed
      }
    }
  } catch (err) {
    console.error('Error leyendo insumos de localStorage:', err)
  }
  return INSUMOS_INICIALES
}

function guardarLocal(lista: InsumoMaestro[]) {
  try {
    localStorage.setItem(CLAVE_LOCAL_INSUMOS, JSON.stringify(lista))
  } catch (err) {
    console.error('Error guardando insumos en localStorage:', err)
  }
}

export function useMaestroInsumos() {
  const [insumos, setInsumos] = useState<InsumoMaestro[]>(() => cargarLocal())
  const [cargando, setCargando] = useState(false)
  const [busqueda, setBusqueda] = useState('')
  const [filtroTipo, setFiltroTipo] = useState<TipoInsumoMaestro | 'todos'>('todos')
  const [filtroCategoria, setFiltroCategoria] = useState<string>('todas')

  // Sincronizar desde Supabase
  const sincronizarDesdeServidor = useCallback(async () => {
    try {
      setCargando(true)
      const { data, error } = await sb
        .from('plan_maestro_insumos')
        .select('*')
        .eq('activo', true)
        .order('tipo', { ascending: true })

      if (!error && data && data.length > 0) {
        const mapeados: InsumoMaestro[] = data.map((row: any) => ({
          id: row.id,
          codigo: row.codigo,
          nombre: row.nombre,
          tipo: row.tipo,
          unidad: row.unidad,
          tarifa_base: Number(row.tarifa_base || 0),
          categoria: row.categoria || 'General',
          activo: Boolean(row.activo),
          created_at: row.created_at,
          updated_at: row.updated_at,
        }))
        setInsumos(mapeados)
        guardarLocal(mapeados)
      }
    } catch (err) {
      console.warn('Usando catálogo local de insumos:', err)
    } finally {
      setCargando(false)
    }
  }, [])

  useEffect(() => {
    void sincronizarDesdeServidor()
  }, [sincronizarDesdeServidor])

  // Crear insumo
  const crearInsumo = useCallback((nuevo: Partial<InsumoMaestro>): InsumoMaestro => {
    const item: InsumoMaestro = {
      id: nuevo.id || crypto.randomUUID(),
      codigo: nuevo.codigo || `INS-${Date.now().toString().slice(-4)}`,
      nombre: nuevo.nombre || 'Nuevo Insumo',
      tipo: nuevo.tipo || 'material',
      unidad: nuevo.unidad || 'UND',
      tarifa_base: Math.max(0, Number(nuevo.tarifa_base || 0)),
      categoria: nuevo.categoria || 'General',
      activo: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }

    setInsumos((prev) => {
      const actualizados = [item, ...prev]
      guardarLocal(actualizados)
      return actualizados
    })

    void (async () => {
      try {
        await sb.from('plan_maestro_insumos').insert({
          id: item.id,
          codigo: item.codigo,
          nombre: item.nombre,
          tipo: item.tipo,
          unidad: item.unidad,
          tarifa_base: item.tarifa_base,
          categoria: item.categoria,
          activo: true,
        })
      } catch (err) {
        console.error('Error insertando insumo en Supabase:', err)
      }
    })()

    return item
  }, [])

  // Actualizar un insumo con opción de recálculo en cascada
  const actualizarInsumo = useCallback(
    (id: string, cambios: Partial<InsumoMaestro>, propagarCascada = true): { insumo: InsumoMaestro; apusAfectados: number } => {
      let insumoActualizado: InsumoMaestro | null = null

      setInsumos((prev) => {
        const actualizados = prev.map((item) => {
          if (item.id !== id) return item
          insumoActualizado = {
            ...item,
            ...cambios,
            updated_at: new Date().toISOString(),
          }
          return insumoActualizado
        })
        guardarLocal(actualizados)
        return actualizados
      })

      // Sincronizar en BD
      void sb
        .from('plan_maestro_insumos')
        .update({
          nombre: cambios.nombre,
          tipo: cambios.tipo,
          unidad: cambios.unidad,
          tarifa_base: cambios.tarifa_base,
          categoria: cambios.categoria,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)

      let conteoAfectados = 0

      // Efecto en Cascada: Actualiza todos los APUs que usan este insumo
      if (propagarCascada && insumoActualizado && cambios.tarifa_base !== undefined) {
        try {
          const rawApus = localStorage.getItem(CLAVE_LOCAL_APUS)
          if (rawApus) {
            const listaApus: Apu[] = JSON.parse(rawApus)
            const nuevaTarifa = Number(cambios.tarifa_base)

            const apusRecalculados = listaApus.map((apu) => {
              let apuCambio = false

              // 1. Materiales
              const materialesActualizados = (apu.materiales || []).map((m) => {
                if (m.codigo === insumoActualizado!.codigo || m.descripcion.toLowerCase() === insumoActualizado!.nombre.toLowerCase()) {
                  apuCambio = true
                  return { ...m, costo_base: nuevaTarifa }
                }
                return m
              })

              // 2. Equipos
              const equiposActualizados = (apu.equipos || []).map((eq) => {
                if (eq.descripcion.toLowerCase() === insumoActualizado!.nombre.toLowerCase()) {
                  apuCambio = true
                  return { ...eq, tarifa: nuevaTarifa }
                }
                return eq
              })

              // 3. Transportes
              const transportesActualizados = (apu.transportes || []).map((tr) => {
                if (tr.descripcion.toLowerCase() === insumoActualizado!.nombre.toLowerCase()) {
                  apuCambio = true
                  return { ...tr, costo_tarifa: nuevaTarifa }
                }
                return tr
              })

              // 4. Mano de Obra
              const manoObraActualizada = (apu.mano_obra || []).map((mo) => {
                if (mo.cargo.toLowerCase() === insumoActualizado!.nombre.toLowerCase()) {
                  apuCambio = true
                  return { ...mo, costo_dia: nuevaTarifa, salario_base: nuevaTarifa }
                }
                return mo
              })

              if (apuCambio) {
                conteoAfectados++
                const recal = recalcularApu({
                  ...apu,
                  materiales: materialesActualizados,
                  equipos: equiposActualizados,
                  transportes: transportesActualizados,
                  mano_obra: manoObraActualizada,
                })
                // Sincronizar en BD en segundo plano
                void sb
                  .from('plan_apus')
                  .update({
                    costo_equipos: recal.costo_equipos,
                    costo_materiales: recal.costo_materiales,
                    costo_transportes: recal.costo_transportes,
                    costo_mano_obra: recal.costo_mano_obra,
                    costo_directo: recal.costo_directo,
                    valor_aiu: recal.valor_aiu,
                    precio_unitario_total: recal.precio_unitario_total,
                    equipos: recal.equipos,
                    materiales: recal.materiales,
                    transportes: recal.transportes,
                    mano_obra: recal.mano_obra,
                    updated_at: new Date().toISOString(),
                  })
                  .eq('id', recal.id)
                return recal
              }
              return apu
            })

            if (conteoAfectados > 0) {
              localStorage.setItem(CLAVE_LOCAL_APUS, JSON.stringify(apusRecalculados))
              window.dispatchEvent(new CustomEvent('mega:banco-apus-actualizado'))
            }
          }
        } catch (err) {
          console.error('Error aplicando recálculo en cascada:', err)
        }
      }

      return { insumo: insumoActualizado!, apusAfectados: conteoAfectados }
    },
    []
  )

  // Eliminar insumo (marcar inactivo)
  const eliminarInsumo = useCallback((id: string) => {
    setInsumos((prev) => {
      const actualizados = prev.filter((i) => i.id !== id)
      guardarLocal(actualizados)
      return actualizados
    })
    void sb.from('plan_maestro_insumos').update({ activo: false }).eq('id', id)
  }, [])

  // Filtrado reactivo
  const insumosFiltrados = useMemo(() => {
    return insumos.filter((item) => {
      if (filtroTipo !== 'todos' && item.tipo !== filtroTipo) return false
      if (filtroCategoria !== 'todas' && item.categoria !== filtroCategoria) return false
      if (busqueda.trim()) {
        const q = busqueda.toLowerCase().trim()
        const matchCodigo = item.codigo.toLowerCase().includes(q)
        const matchNombre = item.nombre.toLowerCase().includes(q)
        const matchCat = (item.categoria || '').toLowerCase().includes(q)
        if (!matchCodigo && !matchNombre && !matchCat) return false
      }
      return true
    })
  }, [insumos, filtroTipo, filtroCategoria, busqueda])

  const categoriasDisponibles = useMemo(() => {
    const cats = new Set<string>()
    insumos.forEach((i) => {
      if (i.categoria) cats.add(i.categoria)
    })
    return Array.from(cats).sort()
  }, [insumos])

  return {
    insumos,
    insumosFiltrados,
    cargando,
    busqueda,
    setBusqueda,
    filtroTipo,
    setFiltroTipo,
    filtroCategoria,
    setFiltroCategoria,
    categoriasDisponibles,
    crearInsumo,
    actualizarInsumo,
    actualizarInsumoEnCascada: (id: string, nuevaTarifa: number) => actualizarInsumo(id, { tarifa_base: nuevaTarifa }, true),
    eliminarInsumo,
    sincronizarDesdeServidor,
  }
}

